import React from "react";
import {
  TableRow,
  TableCell,
  Grid,
  IconButton,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { Form } from "@components";
import { useDispatch } from "react-redux";
import { Delete as DeleteIcon, Edit as EditIcon } from "@material-ui/icons";
import { UserDelegate, updateUserDelegate, deleteUserDelegate } from "@store";

type Props = {
  delegate: UserDelegate;
};

type State = {
  deleting: boolean;
  editing: boolean;
  form: {
    values: {
      active: boolean;
      email: string;
    };
    errors: {
      active?: Error | undefined;
      email?: Error | undefined;
    };
  };
  mode: "read" | "write";
};

type Action =
  | {
      type: "reset";
      delegate: UserDelegate;
    }
  | {
      type: "set-mode";
      mode: State["mode"];
    }
  | {
      type: "set-value";
      key: keyof State["form"]["values"];
      value: any; // [TODO] Fix type
    }
  | {
      type: "toggle-deleting";
      value?: State["deleting"];
    }
  | {
      type: "toggle-editing";
      value?: State["editing"];
    };

function init(delegate: UserDelegate): State {
  return {
    deleting: false,
    editing: false,
    form: {
      values: {
        active: delegate.active,
        email: delegate.email,
      },
      errors: {},
    },
    mode: "read",
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "reset":
      return init(action.delegate);
    case "set-mode":
      return {
        ...state,
        mode: action.mode,
      };
    case "set-value":
      return {
        ...state,
        form: {
          values: {
            ...state.form.values,
            [action.key]: action.value,
          },
          errors: {
            ...state.form.errors,
            [action.key]: Form.Validators.hasValue(action.value),
          },
        },
      };
    case "toggle-deleting":
      return {
        ...state,
        deleting: action.value === undefined ? !state.deleting : action.value,
      };
    case "toggle-editing":
      return {
        ...state,
        editing: action.value === undefined ? !state.editing : action.value,
      };
    default:
      return state;
  }
}

export const Delegate: React.FC<Props> = ({ delegate }) => {
  const storeDispatch = useDispatch();
  const [localState, localDispatch] = React.useReducer(reducer, delegate, init);

  React.useEffect(() => {
    localDispatch({
      type: "reset",
      delegate,
    });
  }, [delegate]);

  return (
    <TableRow style={{ height: "47px" }}>
      {localState.mode === "read" ? (
        <React.Fragment>
          <TableCell>
            <Grid container>
              <Grid item>{delegate.email}</Grid>
            </Grid>
          </TableCell>
          <TableCell>
            <Grid container>
              <Grid item xs={12}>
                {delegate.active ? "Active" : "Inactive"}
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="center">
            <IconButton
              size="small"
              disabled={localState.editing || localState.deleting}
              onClick={() => {
                localDispatch({
                  type: "set-mode",
                  mode: "write",
                });
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </TableCell>
        </React.Fragment>
      ) : null}
      {localState.mode === "write" ? (
        <React.Fragment>
          <TableCell>
            <Grid container>
              <Form.Textbox
                error={localState.form.errors.email}
                name="email"
                size={12}
                onChange={(key, value) => {
                  localDispatch({
                    type: "set-value",
                    key,
                    value,
                  });
                }}
                placeholder="email"
                value={localState.form.values.email}
              />
            </Grid>
          </TableCell>
          <TableCell>
            <Grid container>
              <Form.Checkbox
                name="active"
                label="Delegate is active"
                onChange={(key, value) => {
                  localDispatch({
                    type: "set-value",
                    key,
                    value,
                  });
                }}
                size={12}
                value={localState.form.values.active}
              />
            </Grid>
          </TableCell>
          <TableCell align="center">
            <Button
              disabled={localState.deleting || localState.editing}
              onClick={() => {
                const { active, email } = localState.form.values;

                if (
                  email &&
                  (active !== delegate.active || email !== delegate.email)
                ) {
                  localDispatch({
                    type: "toggle-editing",
                    value: true,
                  });
                  storeDispatch(
                    updateUserDelegate({
                      id: delegate.id,
                      email,
                      active,
                    })
                  );
                } else {
                  localDispatch({
                    type: "reset",
                    delegate,
                  });
                }
              }}
              size="small"
              variant="outlined"
            >
              {localState.editing ? <CircularProgress size={20} /> : "OK"}
            </Button>
          </TableCell>
        </React.Fragment>
      ) : null}
      <TableCell align="center">
        <IconButton
          size="small"
          disabled={localState.deleting || localState.editing}
          onClick={() => {
            localDispatch({
              type: "toggle-deleting",
              value: true,
            });
            storeDispatch(deleteUserDelegate(delegate.id));
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};
